import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { FileCheck, Loader2, FileText, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const MOTIF_LABELS = { medical: 'Raison médicale', demenagement: 'Déménagement', autre: 'Autre' };
const STATUS_LABELS = { pending: 'En attente', approved: 'Acceptée', refused: 'Refusée' };

export default function ResiliationAdminPanel() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [decide, setDecide] = useState(null); // {id, decision}
  const [note, setNote] = useState('');
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/resiliation/requests`, { withCredentials: true });
      setItems(res.data.items || []);
      setPending(res.data.pending || 0);
    } catch (e) { /* silencieux */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const viewDoc = (id) => window.open(`${API}/admin/resiliation/${id}/document`, '_blank');

  const submitDecision = async () => {
    if (!decide) return;
    setWorking(true);
    try {
      await axios.post(`${API}/admin/resiliation/${decide.id}/decision`,
        { decision: decide.decision, admin_note: note }, { withCredentials: true });
      toast.success(decide.decision === 'approved' ? 'Résiliation acceptée.' : 'Demande refusée.');
      setDecide(null); setNote('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible.');
    } finally { setWorking(false); }
  };

  return (
    <Card className="border border-[#E9DDCC] rounded-2xl bg-white" data-testid="resiliation-admin-panel">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileCheck className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Résiliations</span>
          </div>
          {pending > 0 && <span className="text-xs bg-[#C76542] text-white rounded-full px-2.5 py-0.5" data-testid="resiliation-pending-badge">{pending} en attente</span>}
        </div>
        <h3 className="font-heading text-lg text-[#493329]">Demandes de résiliation</h3>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#AE9885]" data-testid="resiliation-empty">Aucune demande de résiliation.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[#E9DDCC]">
            <table className="w-full text-xs" data-testid="resiliation-table">
              <thead className="bg-[#493329] text-white">
                <tr>
                  <th className="px-2 py-2 text-left">Cliente</th>
                  <th className="px-2 py-2 text-left">Motif</th>
                  <th className="px-2 py-2 text-left">Message</th>
                  <th className="px-2 py-2">Pièce</th>
                  <th className="px-2 py-2">Date</th>
                  <th className="px-2 py-2">Statut</th>
                  <th className="px-2 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EFE4D0]">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-[#FAF6EF]" data-testid={`resiliation-row-${r.id}`}>
                    <td className="px-2 py-2 whitespace-nowrap">{r.name || r.email}</td>
                    <td className="px-2 py-2">{MOTIF_LABELS[r.motif] || r.motif}</td>
                    <td className="px-2 py-2 max-w-[220px] truncate" title={r.message}>{r.message || '—'}</td>
                    <td className="px-2 py-2 text-center">
                      {r.has_document
                        ? <button onClick={() => viewDoc(r.id)} className="inline-flex items-center gap-1 text-[#9A744C] hover:underline" data-testid={`view-doc-${r.id}`}><FileText className="w-3.5 h-3.5" /> Voir</button>
                        : <span className="text-[#AE9885]">—</span>}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString('fr-FR')}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={r.status === 'approved' ? 'text-emerald-700' : r.status === 'refused' ? 'text-[#C76542]' : 'text-[#9A744C]'}>
                        {STATUS_LABELS[r.status]}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {r.status === 'pending' ? (
                        <div className="flex gap-1 justify-center">
                          <button onClick={() => { setDecide({ id: r.id, decision: 'approved' }); setNote(''); }}
                            className="inline-flex items-center gap-1 text-emerald-700 hover:underline" data-testid={`approve-${r.id}`}><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => { setDecide({ id: r.id, decision: 'refused' }); setNote(''); }}
                            className="inline-flex items-center gap-1 text-[#C76542] hover:underline" data-testid={`refuse-${r.id}`}><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <span className="text-[#AE9885]">{r.decided_by ? 'traitée' : '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={!!decide} onOpenChange={(o) => !o && setDecide(null)}>
        <DialogContent className="max-w-md" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#493329]">
              {decide?.decision === 'approved' ? 'Accepter la résiliation' : 'Refuser la demande'}
            </DialogTitle>
            <DialogDescription>
              {decide?.decision === 'approved'
                ? "L'abonnement Stripe sera annulé et la cliente passera au statut « Résiliée »."
                : 'La cliente sera informée du refus. Son abonnement reste actif.'}
            </DialogDescription>
          </DialogHeader>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="Note interne / message (facultatif)"
            className="w-full rounded-lg border border-[#E9DDCC] px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C99A55]"
            data-testid="decision-note" />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDecide(null)} data-testid="decision-cancel">Annuler</Button>
            <Button onClick={submitDecision} disabled={working}
              className={decide?.decision === 'approved' ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : 'bg-[#C76542] hover:bg-[#b2573a] text-white'}
              data-testid="decision-confirm">
              {working ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {decide?.decision === 'approved' ? 'Confirmer l\'acceptation' : 'Confirmer le refus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
